import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import StorageIcon from "@mui/icons-material/Storage";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

import {
  getDataStats,
  getDataSyncStatus,
  getSchedulerStatus,
  triggerDataSync,
  triggerSchedulerJob,
  getKiteLoginUrl,
  getKiteStatus,
  triggerKiteSync,
  getKiteSyncStatus,
  triggerKiteBackfill,
} from "../Service/data";

const PRESET_OPTIONS = [
  { value: "nifty_50", label: "Nifty 50" },
  { value: "nifty_500", label: "Nifty 500" },
  { value: "nse_all", label: "NSE All" },
  { value: "crypto_commodities", label: "Crypto & Commodities" },
  { value: "signal_stocks", label: "Signal Stocks" },
];

const formatNumber = (num) => {
  if (num == null || num === undefined) return "—";
  return new Intl.NumberFormat().format(num);
};

const formatBytes = (bytes) => {
  if (bytes == null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const IntervalStatusIcon = ({ status }) => {
  if (status === "done")
    return <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />;
  if (status === "failed")
    return <ErrorIcon sx={{ fontSize: 18, color: "error.main" }} />;
  if (status === "running")
    return (
      <HourglassEmptyIcon
        sx={{ fontSize: 18, color: "warning.main", animation: "spin 1s linear infinite" }}
      />
    );
  return null;
};

const DataSyncPage = () => {
  const theme = useTheme();

  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerError, setSchedulerError] = useState(null);

  const [preset, setPreset] = useState("signal_stocks");
  const [years, setYears] = useState(3);
  const [syncing, setSyncing] = useState(false);

  // Kite state
  const [kiteStatus, setKiteStatus] = useState(null);
  const [kiteIntervals, setKiteIntervals] = useState({ "1m": true, "5m": true, daily: true });
  const [kiteSyncStatus, setKiteSyncStatus] = useState(null);
  const [kiteSyncing, setKiteSyncing] = useState(false);
  const [kiteError, setKiteError] = useState(null);

  const loadStats = useCallback(() => {
    getDataStats()
      .then((data) => {
        if (data && data.error) {
          setStatsError(data.error);
          setStats(null);
        } else {
          setStats(data);
          setStatsError(null);
        }
      })
      .catch((err) => {
        console.error(err);
        setStatsError("Failed to load data stats. Is the backend running?");
        setStats(null);
      });
  }, []);

  const loadSyncStatus = useCallback(() => {
    getDataSyncStatus()
      .then((data) => {
        if (data && data.error) {
          setSyncError(data.error);
          setSyncStatus(null);
        } else {
          setSyncStatus(data);
          setSyncError(null);
        }
      })
      .catch((err) => {
        console.error(err);
        setSyncError("Failed to load sync status.");
        setSyncStatus(null);
      });
  }, []);

  const loadSchedulerStatus = useCallback(() => {
    getSchedulerStatus()
      .then((data) => {
        if (data && data.error) {
          setSchedulerError(data.error);
          setSchedulerStatus(null);
        } else {
          setSchedulerStatus(data);
          setSchedulerError(null);
        }
      })
      .catch((err) => {
        console.error(err);
        setSchedulerError("Failed to load scheduler status.");
        setSchedulerStatus(null);
      });
  }, []);

  const loadKiteStatus = useCallback(() => {
    getKiteStatus()
      .then((data) => setKiteStatus(data))
      .catch(() => setKiteStatus(null));
  }, []);

  const loadKiteSyncStatus = useCallback(() => {
    getKiteSyncStatus()
      .then((data) => {
        setKiteSyncStatus(data);
        if (data && data.running === false && kiteSyncing) {
          setKiteSyncing(false);
          loadStats();
        }
      })
      .catch(() => {});
  }, [kiteSyncing, loadStats]);

  useEffect(() => {
    loadStats();
    loadSchedulerStatus();
    loadKiteStatus();
  }, [loadStats, loadSchedulerStatus, loadKiteStatus]);

  // Check URL params for kite callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("kite=connected")) {
      loadKiteStatus();
      window.location.hash = "#/data-sync";
    }
  }, [loadKiteStatus]);

  useEffect(() => {
    if (syncing) {
      loadSyncStatus();
      const id = setInterval(loadSyncStatus, 2000);
      return () => clearInterval(id);
    } else {
      loadSyncStatus();
    }
  }, [syncing, loadSyncStatus]);

  useEffect(() => {
    if (kiteSyncing) {
      const id = setInterval(loadKiteSyncStatus, 2000);
      return () => clearInterval(id);
    }
  }, [kiteSyncing, loadKiteSyncStatus]);

  const handleStartSync = () => {
    setSyncing(true);
    setSyncError(null);
    triggerDataSync(preset, years)
      .then((data) => {
        if (data && data.error) {
          setSyncError(data.error);
          setSyncing(false);
        } else {
          loadSyncStatus();
          loadStats();
        }
      })
      .catch((err) => {
        console.error(err);
        setSyncError("Failed to start sync.");
        setSyncing(false);
      });
  };

  useEffect(() => {
    if (syncStatus && syncStatus.running === false && syncing) {
      setSyncing(false);
      loadStats();
    }
  }, [syncStatus, syncing, loadStats]);

  const handleRunJob = (jobId) => {
    triggerSchedulerJob(jobId)
      .then((data) => {
        if (data && data.error) {
          console.error(data.error);
        } else {
          loadSchedulerStatus();
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleKiteLogin = () => {
    window.open(getKiteLoginUrl(), "_blank", "width=600,height=600");
    // Poll for connection status after opening login window
    const pollId = setInterval(() => {
      getKiteStatus().then((data) => {
        if (data && data.connected) {
          setKiteStatus(data);
          clearInterval(pollId);
        }
      });
    }, 3000);
    setTimeout(() => clearInterval(pollId), 120000);
  };

  const handleKiteSync = () => {
    setKiteSyncing(true);
    setKiteError(null);
    const intervals = Object.entries(kiteIntervals)
      .filter(([, v]) => v)
      .map(([k]) => k);
    triggerKiteSync("^NSEI", intervals)
      .then((data) => {
        if (data && data.error) {
          setKiteError(data.error);
          setKiteSyncing(false);
        } else {
          loadKiteSyncStatus();
        }
      })
      .catch((err) => {
        setKiteError(String(err));
        setKiteSyncing(false);
      });
  };

  const handleKiteBackfill = () => {
    setKiteSyncing(true);
    setKiteError(null);
    const intervals = Object.entries(kiteIntervals)
      .filter(([, v]) => v)
      .map(([k]) => k);
    triggerKiteBackfill("^NSEI", intervals)
      .then((data) => {
        if (data && data.error) {
          setKiteError(data.error);
          setKiteSyncing(false);
        } else {
          loadKiteSyncStatus();
        }
      })
      .catch((err) => {
        setKiteError(String(err));
        setKiteSyncing(false);
      });
  };

  const jobs = schedulerStatus?.jobs ?? [];
  const progress =
    syncStatus?.total != null && syncStatus?.total > 0
      ? (syncStatus.completed ?? 0) / syncStatus.total
      : 0;

  const kiteConnected = kiteStatus?.connected === true;

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: "100vw",
        minHeight: "100vh",
        background: theme.palette.background.default,
        p: 3,
      }}
    >
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Data Sync & Management
      </Typography>

      {/* Stats Section */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <StorageIcon /> Database Stats
        </Typography>
        {statsError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {statsError}
          </Typography>
        )}
        {stats && (
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Total Symbols
              </Typography>
              <Typography variant="h6">
                {formatNumber(stats.total_symbols)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Total Candles
              </Typography>
              <Typography variant="h6">
                {formatNumber(
                  (stats.total_daily_candles ?? 0) +
                    (stats.total_intraday_candles ?? 0) || stats.total_candles
                )}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                DB Size
              </Typography>
              <Typography variant="h6">
                {stats.db_size_mb != null
                  ? `${stats.db_size_mb} MB`
                  : formatBytes(stats.db_size_bytes)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Date Range
              </Typography>
              <Typography variant="h6">
                {stats.daily_date_range?.start && stats.daily_date_range?.end
                  ? `${stats.daily_date_range.start} — ${stats.daily_date_range.end}`
                  : stats.date_min && stats.date_max
                  ? `${stats.date_min} — ${stats.date_max}`
                  : "—"}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Card>

      {/* Zerodha Kite Connect Section */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {kiteConnected ? (
              <LinkIcon color="success" />
            ) : (
              <LinkOffIcon color="disabled" />
            )}
            Zerodha Kite Connect
          </Typography>
          {kiteConnected ? (
            <Chip
              label={`Connected — ${kiteStatus?.user?.user_name || kiteStatus?.user?.user_id || "OK"}`}
              color="success"
              size="small"
              variant="outlined"
            />
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<LinkIcon />}
              onClick={handleKiteLogin}
              size="small"
            >
              Connect Zerodha
            </Button>
          )}
        </Box>

        {!kiteConnected && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Connect your Zerodha account to sync Nifty Spot data at 1-minute,
            5-minute, and daily intervals. Access token expires daily at 6 AM —
            you'll need to reconnect each morning.
          </Alert>
        )}

        {kiteConnected && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Nifty 50 Index (^NSEI) Data Sync
            </Typography>

            <FormGroup row sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={kiteIntervals["1m"]}
                    onChange={(e) =>
                      setKiteIntervals((p) => ({ ...p, "1m": e.target.checked }))
                    }
                    disabled={kiteSyncing}
                    size="small"
                  />
                }
                label="1 Minute"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={kiteIntervals["5m"]}
                    onChange={(e) =>
                      setKiteIntervals((p) => ({ ...p, "5m": e.target.checked }))
                    }
                    disabled={kiteSyncing}
                    size="small"
                  />
                }
                label="5 Minute"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={kiteIntervals.daily}
                    onChange={(e) =>
                      setKiteIntervals((p) => ({
                        ...p,
                        daily: e.target.checked,
                      }))
                    }
                    disabled={kiteSyncing}
                    size="small"
                  />
                }
                label="Daily"
              />
            </FormGroup>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={handleKiteSync}
                disabled={kiteSyncing}
                size="small"
              >
                {kiteSyncing ? "Syncing..." : "Quick Sync (Recent)"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudDownloadIcon />}
                onClick={handleKiteBackfill}
                disabled={kiteSyncing}
                size="small"
              >
                {kiteSyncing ? "Running..." : "Deep Backfill (All History)"}
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Quick Sync: fetches last 60 days (1m), 100 days (5m), 2000 days (daily).
              Deep Backfill: fetches all available history back to ~2015.
            </Typography>

            {kiteError && (
              <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                {kiteError}
              </Alert>
            )}

            {kiteSyncStatus && kiteSyncStatus.status !== "idle" && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
                  <Chip
                    label={kiteSyncStatus.status?.toUpperCase()}
                    size="small"
                    color={
                      kiteSyncStatus.status === "completed"
                        ? "success"
                        : kiteSyncStatus.status === "failed"
                        ? "error"
                        : "warning"
                    }
                  />
                  {kiteSyncStatus.symbol && (
                    <Typography variant="body2" color="text.secondary">
                      Symbol: {kiteSyncStatus.symbol}
                    </Typography>
                  )}
                  {kiteSyncStatus.mode === "backfill" && (
                    <Chip label="BACKFILL" size="small" variant="outlined" />
                  )}
                </Box>

                {kiteSyncStatus.progress && (
                  <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                    {Object.entries(kiteSyncStatus.progress).map(([interval, status]) => (
                      <Box
                        key={interval}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <IntervalStatusIcon status={status} />
                        <Typography variant="body2">
                          {interval}: {status}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {kiteSyncStatus.log && kiteSyncStatus.log.length > 0 && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      maxHeight: 200,
                      overflow: "auto",
                      fontFamily: "monospace",
                      fontSize: 12,
                    }}
                  >
                    {kiteSyncStatus.log.map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </Box>
                )}

                {kiteSyncing && (
                  <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
                )}
              </Box>
            )}
          </>
        )}
      </Card>

      {/* Yahoo/Binance Sync Section */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Sync Data (Yahoo / Binance)
        </Typography>
        {syncError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {syncError}
          </Typography>
        )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="preset-select-label">Preset</InputLabel>
              <Select
                labelId="preset-select-label"
                value={preset}
                label="Preset"
                onChange={(e) => setPreset(e.target.value)}
                disabled={syncing}
              >
                {PRESET_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Years of history"
              type="number"
              value={years}
              onChange={(e) =>
                setYears(Math.max(1, Math.min(20, Number(e.target.value) || 1)))
              }
              size="small"
              inputProps={{ min: 1, max: 20 }}
              disabled={syncing}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartSync}
              disabled={syncing}
              fullWidth
            >
              {syncing ? "Syncing..." : "Start Sync"}
            </Button>
          </Grid>
        </Grid>
        {syncing && syncStatus && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {formatNumber(syncStatus.completed ?? 0)} /{" "}
              {formatNumber(syncStatus.total ?? 0)} symbols
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress * 100}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        )}
      </Card>

      {/* Scheduler Section */}
      <Card
        sx={{
          mb: 3,
          p: 2,
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Scheduled Jobs
        </Typography>
        {schedulerError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {schedulerError}
          </Typography>
        )}
        {jobs.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Next Run</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <Chip label={job.id} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {job.next_run_time ?? job.next_run
                      ? new Date(
                          job.next_run_time ?? job.next_run
                        ).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handleRunJob(job.id)}
                    >
                      Run Now
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          !schedulerError && (
            <Typography color="text.secondary">
              No scheduled jobs found.
            </Typography>
          )
        )}
      </Card>
    </Box>
  );
};

export default DataSyncPage;
